import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  InputAdornment,
  CircularProgress,
  Alert,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { Close, ArrowForward, ArrowBack, CheckCircle, Calculate, LocationOn, Info, Edit, AttachFile, Business } from '@mui/icons-material';
import { Scope2Method } from '../../types';
import { useApp } from '../../contexts/AuthContext';
import {
  SCOPE1_CATEGORIES,
  SCOPE2_ENERGY_TYPES,
  emissionFactorService,
  getFuelTypesForActivity,
  getUnitsForFuelType,
} from '../../services/emissionFactorService';
import { attachmentService, Attachment } from '../../services/emissionsService';
import { FileUploadZone, UploadedFile } from '../shared/FileUploadZone';

interface ActivityDataEntryDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  scope: 'Scope 1' | 'Scope 2';
  editEntry?: {
    id: string;
    scope: string;
    category: string;
    activity?: string;  // Activity type key (e.g., 'gaseous_fuels', 'refrigerant_leakage')
    activity_type: string;  // Display value (subcategory/fuel type)
    fuel_type?: string;
    activity_data: number;
    activity_unit: string;
    facility_id: string;
    year: number;
    month: number;
    // Scope 2 specific fields
    method?: string;
    renewable_share?: number;
    grid_region?: string;
    // Common fields
    emission_factor_value?: number;
    emission_factor_source?: string;
    notes?: string;
  } | null;
}

const steps = ['Select Facility & Category', 'Enter Activity Data', 'Review & Submit'];

export const ActivityDataEntryDialog: React.FC<ActivityDataEntryDialogProps> = ({
  open,
  onClose,
  onSubmit,
  scope,
  editEntry,
}) => {
  const isEditMode = !!editEntry;
  const { facilities, selectedFacility, user } = useApp();
  const [activeStep, setActiveStep] = useState(0);
  const currentDate = new Date();
  // Emission factor source type
  // 'standard' represents the default source (EPA for mobile combustion, DEFRA for others)
  type EmissionFactorSourceType = 'auto' | 'standard' | 'location' | 'custom';

  const [formData, setFormData] = useState({
    facilityId: selectedFacility?.id || '',
    category: '',
    activity: '',
    fuelType: '',
    activityAmount: '',
    activityUnit: '',
    method: 'Location-Based' as Scope2Method,
    renewableShare: '',
    country: 'United Kingdom',
    notes: '',
    year: currentDate.getFullYear(),
    month: currentDate.getMonth() + 1,
  });

  // Emission factor state
  const [emissionFactor, setEmissionFactor] = useState<number | null>(null);
  const [emissionFactorSource, setEmissionFactorSource] = useState<string>('');
  const [loadingFactor, setLoadingFactor] = useState(false);
  const [factorError, setFactorError] = useState<string | null>(null);

  // Emission factor source selection
  const [factorSourceType, setFactorSourceType] = useState<EmissionFactorSourceType>('auto');
  const [customFactorValue, setCustomFactorValue] = useState<string>('');
  const [availableFactorSources, setAvailableFactorSources] = useState<{
    standard?: { value: number; source: string };  // EPA for mobile combustion, DEFRA for others
    location?: { value: number; source: string; region?: string };
  }>({});

  // Attachment state
  const [attachments, setAttachments] = useState<UploadedFile[]>([]);

  // Data quality state - defaults to 'primary' if evidence attached, 'secondary' otherwise
  const [dataQuality, setDataQuality] = useState<'primary' | 'secondary' | 'tertiary'>('secondary');

  // Track if we're initializing edit mode (using ref to avoid useEffect timing issues)
  const isInitializingEditRef = useRef(false);

  // Pre-populate form data when editing an existing entry
  useEffect(() => {
    if (editEntry && open) {
      // Set the ref BEFORE setting form data to prevent cascading resets
      isInitializingEditRef.current = true;

      // Map backend category values back to frontend values
      const scope1CategoryMap: Record<string, string> = {
        'Stationary Combustion': 'stationary_combustion',
        'Mobile Combustion': 'mobile_combustion',
        'Fugitive Emissions': 'fugitive_emissions',
        'Process Emissions': 'process_emissions',
      };

      const scope2CategoryMap: Record<string, string> = {
        'Purchased Electricity': 'electricity',
        'Purchased Heat': 'heat',
        'Purchased Steam': 'steam',
        'Purchased Cooling': 'cooling',
      };

      const isScope1 = editEntry.scope === 'scope_1';
      const categoryValue = isScope1
        ? scope1CategoryMap[editEntry.category] || editEntry.category
        : scope2CategoryMap[editEntry.category] || editEntry.category;

      // For Scope 1: use editEntry.activity (activity type key like 'gaseous_fuels')
      // Fall back to activity_type if activity is not set (for backwards compatibility)
      const activityValue = isScope1 ? (editEntry.activity || editEntry.activity_type || '') : '';
      // For fuel type, use fuel_type if available, otherwise use activity_type as fallback
      const fuelTypeValue = isScope1 ? (editEntry.fuel_type || editEntry.activity_type || '') : '';

      // For Scope 2: populate method and renewable share from entry
      const methodValue = !isScope1 && editEntry.method ? editEntry.method as Scope2Method : 'Location-Based' as Scope2Method;
      const renewableShareValue = !isScope1 && editEntry.renewable_share !== undefined ? editEntry.renewable_share.toString() : '';

      setFormData({
        facilityId: editEntry.facility_id,
        category: categoryValue,
        activity: activityValue,
        fuelType: fuelTypeValue,
        activityAmount: editEntry.activity_data.toString(),
        activityUnit: editEntry.activity_unit,
        method: methodValue,
        renewableShare: renewableShareValue,
        country: 'United Kingdom',
        notes: editEntry.notes || '',  // Populate notes from entry
        year: editEntry.year,
        month: editEntry.month,
      });

      // Populate emission factor if it was custom
      if (editEntry.emission_factor_value !== undefined && editEntry.emission_factor_value > 0) {
        setEmissionFactor(editEntry.emission_factor_value);
        setEmissionFactorSource(editEntry.emission_factor_source || 'Custom');
        // If source is Custom, set the custom factor input
        if (editEntry.emission_factor_source === 'Custom') {
          setFactorSourceType('custom');
          setCustomFactorValue(editEntry.emission_factor_value.toString());
        }
      }

      // Reset the flag after a brief delay to allow the form to settle
      setTimeout(() => {
        isInitializingEditRef.current = false;
      }, 200);
    }
  }, [editEntry, open]);

  // Get activities for selected category
  const getActivitiesForCategory = useCallback(() => {
    const category = SCOPE1_CATEGORIES.find(c => c.value === formData.category);
    return category?.activities || [];
  }, [formData.category]);

  // Get fuel types for selected activity
  const getFuelTypes = useCallback(() => {
    return getFuelTypesForActivity(formData.category, formData.activity);
  }, [formData.category, formData.activity]);

  // Get units for selected fuel type
  const getUnits = useCallback(() => {
    return getUnitsForFuelType(formData.category, formData.activity, formData.fuelType);
  }, [formData.category, formData.activity, formData.fuelType]);

  // Location-based grid emission factors by region
  const GRID_EMISSION_FACTORS: Record<string, { value: number; source: string }> = {
    'IN': { value: 0.710, source: 'CEA 2024 (India)' },
    'GB': { value: 0.207, source: 'DEFRA 2024 (UK)' },
    'DE': { value: 0.380, source: 'UBA 2024 (Germany)' },
    'US': { value: 0.389, source: 'EPA eGRID 2024 (USA)' },
    'FR': { value: 0.052, source: 'ADEME 2024 (France)' },
    'CN': { value: 0.581, source: 'MEE 2024 (China)' },
    'JP': { value: 0.457, source: 'MOE 2024 (Japan)' },
    'AU': { value: 0.680, source: 'CER 2024 (Australia)' },
    'CA': { value: 0.120, source: 'ECCC 2024 (Canada)' },
    'BR': { value: 0.074, source: 'MCTI 2024 (Brazil)' },
  };

  // Fetch emission factors from multiple sources
  useEffect(() => {
    const fetchEmissionFactors = async () => {
      if (scope === 'Scope 1' && formData.category && formData.activity && formData.fuelType && formData.activityUnit) {
        setLoadingFactor(true);
        setFactorError(null);
        try {
          // Fetch emission factor (EPA for mobile combustion, DEFRA for others)
          const result = await emissionFactorService.getFactorValue(
            1,
            formData.category,
            formData.activity,
            formData.fuelType,
            formData.activityUnit,
            user?.companyId
          );

          // Store standard factor (EPA for mobile combustion, DEFRA for others)
          setAvailableFactorSources({
            standard: { value: result.value, source: result.source },
          });

          // Apply factor based on selected source type
          if (factorSourceType === 'custom' && customFactorValue) {
            setEmissionFactor(parseFloat(customFactorValue));
            setEmissionFactorSource('Custom');
          } else {
            setEmissionFactor(result.value);
            setEmissionFactorSource(result.source);
          }
        } catch (error) {
          console.error('Failed to fetch emission factor:', error);
          setFactorError('Could not load emission factor');
          setEmissionFactor(null);
        } finally {
          setLoadingFactor(false);
        }
      } else if (scope === 'Scope 2' && formData.category && formData.facilityId) {
        setLoadingFactor(true);
        setFactorError(null);
        try {
          const selectedFac = facilities.find(f => f.id === formData.facilityId);
          // Get grid region - check electricity_grid first, then try to map from country
          let gridRegion = selectedFac?.electricity_grid;

          // Country can be either 2-letter code (IN, GB) or full name (India, United Kingdom)
          const countryToGrid: Record<string, string> = {
            // 2-letter codes (database format)
            'IN': 'IN',
            'GB': 'GB',
            'DE': 'DE',
            'US': 'US',
            'FR': 'FR',
            'CN': 'CN',
            'JP': 'JP',
            'AU': 'AU',
            'CA': 'CA',
            'BR': 'BR',
            // Full names
            'India': 'IN',
            'United Kingdom': 'GB',
            'UK': 'GB',
            'Germany': 'DE',
            'United States': 'US',
            'USA': 'US',
            'France': 'FR',
            'China': 'CN',
            'Japan': 'JP',
            'Australia': 'AU',
            'Canada': 'CA',
            'Brazil': 'BR',
          };

          const facilityCountry = selectedFac?.country || '';
          const expectedGridCode = countryToGrid[facilityCountry] || '';

          // Override electricity_grid if it doesn't match the country
          if (!gridRegion || (expectedGridCode && !gridRegion.startsWith(expectedGridCode))) {
            gridRegion = expectedGridCode || gridRegion || 'GB';
          }

          console.log('Scope 2 Emission Factor Lookup:', {
            facility: selectedFac?.name,
            country: selectedFac?.country,
            electricity_grid: selectedFac?.electricity_grid,
            derivedGridRegion: gridRegion,
          });

          // Fetch both standard and location-based factors
          const sources: {
            standard?: { value: number; source: string };
            location?: { value: number; source: string; region?: string };
          } = {};

          // 1. Fetch standard factor (DEFRA for Scope 2 electricity)
          try {
            const standardResult = await emissionFactorService.lookupFactor({
              scope: 2,
              category: 'purchased_electricity',
              activity: 'electricity_generated',
            });
            if (standardResult?.found && standardResult.factor) {
              sources.standard = {
                value: standardResult.factor.value,
                source: standardResult.factor.source || 'DEFRA 2024',
              };
            } else {
              // Fallback DEFRA value
              sources.standard = { value: 0.21233, source: 'DEFRA 2024' };
            }
          } catch {
            sources.standard = { value: 0.21233, source: 'DEFRA 2024' };
          }

          // 2. Get location-based factor for the facility's region
          const locationFactor = GRID_EMISSION_FACTORS[gridRegion];
          if (locationFactor) {
            sources.location = {
              ...locationFactor,
              region: gridRegion,
            };
            console.log('Found location-based factor:', sources.location);
          } else {
            // Try to fetch from backend by region
            try {
              const regionResult = await emissionFactorService.lookupFactor({
                scope: 2,
                category: 'purchased_electricity',
                activity: 'electricity_generated',
                region: gridRegion,
              });
              if (regionResult?.found && regionResult.factor) {
                sources.location = {
                  value: regionResult.factor.value,
                  source: regionResult.factor.source || `Grid (${gridRegion})`,
                  region: gridRegion,
                };
              }
            } catch {
              // No location factor available
              console.log('No location-based factor found for region:', gridRegion);
            }
          }

          setAvailableFactorSources(sources);

          // Apply factor based on selected source type
          let appliedFactor: number | null = null;
          let appliedSource = '';

          if (factorSourceType === 'custom' && customFactorValue) {
            appliedFactor = parseFloat(customFactorValue);
            appliedSource = 'Custom';
          } else if (factorSourceType === 'location' && sources.location) {
            appliedFactor = sources.location.value;
            appliedSource = sources.location.source;
          } else if (factorSourceType === 'standard' && sources.standard) {
            appliedFactor = sources.standard.value;
            appliedSource = sources.standard.source;
          } else if (factorSourceType === 'auto') {
            // Auto: prefer location-based for Scope 2 electricity
            if (sources.location) {
              appliedFactor = sources.location.value;
              appliedSource = sources.location.source;
            } else if (sources.standard) {
              appliedFactor = sources.standard.value;
              appliedSource = sources.standard.source;
            }
          }

          console.log('Applied emission factor:', { appliedFactor, appliedSource, factorSourceType });

          setEmissionFactor(appliedFactor);
          setEmissionFactorSource(appliedSource);
        } catch (error) {
          console.error('Failed to fetch Scope 2 emission factors:', error);
          setFactorError('Could not load emission factor');
        } finally {
          setLoadingFactor(false);
        }
      }
    };

    fetchEmissionFactors();
  }, [scope, formData.category, formData.activity, formData.fuelType, formData.activityUnit, formData.method, formData.facilityId, facilities, user?.companyId, factorSourceType, customFactorValue]);

  // Handle factor source type change
  const handleFactorSourceChange = (newSourceType: EmissionFactorSourceType) => {
    setFactorSourceType(newSourceType);

    if (newSourceType === 'custom') {
      // Keep current factor as default custom value
      if (emissionFactor && !customFactorValue) {
        setCustomFactorValue(emissionFactor.toString());
      }
    } else if (newSourceType === 'standard' && availableFactorSources.standard) {
      setEmissionFactor(availableFactorSources.standard.value);
      setEmissionFactorSource(availableFactorSources.standard.source);
    } else if (newSourceType === 'location' && availableFactorSources.location) {
      setEmissionFactor(availableFactorSources.location.value);
      setEmissionFactorSource(availableFactorSources.location.source);
    } else if (newSourceType === 'auto') {
      // Re-trigger fetch
      if (scope === 'Scope 2' && availableFactorSources.location) {
        setEmissionFactor(availableFactorSources.location.value);
        setEmissionFactorSource(availableFactorSources.location.source);
      } else if (availableFactorSources.standard) {
        setEmissionFactor(availableFactorSources.standard.value);
        setEmissionFactorSource(availableFactorSources.standard.source);
      }
    }
  };

  // Handle custom factor value change
  const handleCustomFactorChange = (value: string) => {
    setCustomFactorValue(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      setEmissionFactor(numValue);
      setEmissionFactorSource('Custom');
    }
  };

  // Auto-set default unit when fuel type changes
  useEffect(() => {
    if (scope === 'Scope 1' && formData.fuelType) {
      const fuelTypes = getFuelTypes();
      const selected = fuelTypes.find(f => f.value === formData.fuelType);
      if (selected && !formData.activityUnit) {
        setFormData(prev => ({ ...prev, activityUnit: selected.defaultUnit }));
      }
    }
    if (scope === 'Scope 2' && formData.category) {
      const selected = SCOPE2_ENERGY_TYPES.find(s => s.value === formData.category);
      if (selected) {
        setFormData(prev => ({ ...prev, activityUnit: selected.unit }));
      }
    }
  }, [formData.fuelType, formData.category, scope, getFuelTypes]);

  // Clear dependent fields when category changes (but not during edit initialization)
  useEffect(() => {
    if (scope === 'Scope 1' && !isInitializingEditRef.current) {
      setFormData(prev => ({
        ...prev,
        activity: '',
        fuelType: '',
        activityUnit: '',
      }));
    }
  }, [formData.category, scope]);

  // Clear fuel type when activity changes (but not during edit initialization)
  useEffect(() => {
    if (scope === 'Scope 1' && formData.activity && !isInitializingEditRef.current) {
      setFormData(prev => ({
        ...prev,
        fuelType: '',
        activityUnit: '',
      }));
    }
  }, [formData.activity, scope]);

  const calculateEmissions = () => {
    const amount = parseFloat(formData.activityAmount) || 0;
    const factor = emissionFactor || 0;
    // Convert to tonnes CO2e (factor is in kg CO2e per unit)
    return (amount * factor) / 1000;
  };

  // Handle file upload
  const handleFileUpload = async (files: File[]): Promise<UploadedFile[]> => {
    const scopeNumber = scope === 'Scope 1' ? 1 : 2;
    const uploaded = await attachmentService.uploadFiles(
      files,
      formData.year,
      formData.month,
      scopeNumber
    );
    return uploaded.map((a: Attachment) => ({
      id: a.id,
      original_filename: a.original_filename,
      file_size_bytes: a.file_size_bytes,
      file_type: a.file_type,
      uploaded_by: a.uploaded_by,
      uploaded_at: a.uploaded_at,
    }));
  };

  // Handle file removal
  const handleFileRemove = async (fileId: string): Promise<void> => {
    await attachmentService.deleteAttachment(fileId);
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleSubmit = () => {
    const emissions = calculateEmissions();
    const selectedFacilityData = facilities.find(f => f.id === formData.facilityId);

    // Build fuel_type field based on scope
    let fuelTypeForBackend = formData.fuelType;
    if (scope === 'Scope 2') {
      fuelTypeForBackend = formData.category;
    }

    const submissionData = {
      scope,
      facilityId: formData.facilityId,
      facilityName: selectedFacilityData?.name,
      category: formData.category,
      activity: formData.activity || undefined,
      subcategory: formData.fuelType || formData.category,
      fuel_type: fuelTypeForBackend,
      activityAmount: parseFloat(formData.activityAmount),
      activityUnit: formData.activityUnit,
      emissionFactor: emissionFactor,
      emissionFactorSource: emissionFactorSource || 'DEFRA 2024',
      value: emissions,
      unit: 'tCO2e',
      method: scope === 'Scope 2' ? formData.method : undefined,
      energySource: scope === 'Scope 2' ? formData.category : undefined,
      consumptionMWh: scope === 'Scope 2' && formData.activityUnit === 'kWh'
        ? parseFloat(formData.activityAmount) / 1000
        : undefined,
      renewableShare: scope === 'Scope 2' && formData.renewableShare
        ? parseFloat(formData.renewableShare)
        : undefined,
      notes: formData.notes,
      status: 'submitted',
      dataSource: attachments.length > 0 ? 'measured' : 'calculated',
      dataQuality: dataQuality,
      methodology: 'GHG Protocol',
      year: formData.year,
      month: formData.month,
      attachment_ids: attachments.map(a => a.id),
    };

    onSubmit(submissionData);
    handleClose();
  };

  const handleClose = () => {
    setActiveStep(0);
    isInitializingEditRef.current = false;
    const now = new Date();
    setFormData({
      facilityId: selectedFacility?.id || '',
      category: '',
      activity: '',
      fuelType: '',
      activityAmount: '',
      activityUnit: '',
      method: 'Location-Based',
      renewableShare: '',
      country: 'United Kingdom',
      notes: '',
      year: now.getFullYear(),
      month: now.getMonth() + 1,
    });
    setEmissionFactor(null);
    setEmissionFactorSource('');
    setFactorError(null);
    setFactorSourceType('auto');
    setCustomFactorValue('');
    setAvailableFactorSources({});
    setAttachments([]);
    setDataQuality('secondary');
    onClose();
  };

  const canProceed = () => {
    if (activeStep === 0) {
      if (scope === 'Scope 1') {
        // facilityId can be empty (organization-wide)
        return formData.category && formData.activity && formData.fuelType;
      }
      return formData.category;
    }
    if (activeStep === 1) {
      return formData.activityAmount && parseFloat(formData.activityAmount) > 0 && formData.activityUnit;
    }
    return true;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, overflow: 'hidden', maxHeight: '85vh' }
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 2, py: 1.5,
        backgroundColor: scope === 'Scope 1' ? '#fef3c7' : '#dbeafe',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 600, color: '#111827', fontSize: '0.95rem' }}>
            {isEditMode ? 'Edit' : 'Add'} {scope} Entry
          </Typography>
          <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem' }}>
            {scope === 'Scope 1' ? 'Direct emissions from owned sources' : 'Indirect emissions from purchased energy'}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Stepper */}
      <Box sx={{ px: 2, py: 1.25, backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label} completed={index < activeStep}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: '0.65rem',
                    color: index <= activeStep ? '#111827' : '#9ca3af',
                    fontWeight: index === activeStep ? 600 : 400,
                  },
                  '& .MuiStepIcon-root': {
                    color: index < activeStep ? '#3b82f6' : index === activeStep ? '#3b82f6' : '#e5e7eb',
                    fontSize: 20,
                  },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ p: 2 }}>
        {/* Step 1: Facility & Category Selection */}
        {activeStep === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Facility Selector */}
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '0.8rem' }}>Facility</InputLabel>
              <Select
                value={formData.facilityId}
                label="Facility"
                onChange={(e) => setFormData({ ...formData, facilityId: e.target.value })}
                startAdornment={
                  formData.facilityId === ''
                    ? <Business sx={{ color: '#6b7280', mr: 0.5, ml: -0.5, fontSize: 18 }} />
                    : <LocationOn sx={{ color: '#4caf50', mr: 0.5, ml: -0.5, fontSize: 18 }} />
                }
                sx={{ fontSize: '0.8rem' }}
                displayEmpty
                renderValue={(value) => {
                  if (value === '') {
                    return 'Organization-wide';
                  }
                  const facility = facilities.find(f => f.id === value);
                  return facility?.name || value;
                }}
              >
                {/* Organization-wide option */}
                <MenuItem value="" sx={{ fontSize: '0.8rem', py: 0.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business sx={{ fontSize: 16, color: '#6b7280' }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>Organization-wide</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Not facility-specific</Typography>
                    </Box>
                  </Box>
                </MenuItem>
                {facilities.length === 0 ? (
                  <MenuItem disabled value="no-facilities">
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      No facilities available. Please create a facility first.
                    </Typography>
                  </MenuItem>
                ) : (
                  facilities.map((facility) => (
                    <MenuItem key={facility.id} value={facility.id} sx={{ fontSize: '0.8rem', py: 0.75 }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                          {facility.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          {facility.type} • {facility.country}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {/* Reporting Period: Year and Month */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <FormControl fullWidth required size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Year</InputLabel>
                <Select
                  value={formData.year}
                  label="Year"
                  onChange={(e) => setFormData({ ...formData, year: Number(e.target.value) })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {[2020, 2021, 2022, 2023, 2024, 2025, 2026].map((year) => (
                    <MenuItem key={year} value={year} sx={{ fontSize: '0.8rem', py: 0.5 }}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Month</InputLabel>
                <Select
                  value={formData.month}
                  label="Month"
                  onChange={(e) => setFormData({ ...formData, month: Number(e.target.value) })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {[
                    { value: 1, label: 'January' },
                    { value: 2, label: 'February' },
                    { value: 3, label: 'March' },
                    { value: 4, label: 'April' },
                    { value: 5, label: 'May' },
                    { value: 6, label: 'June' },
                    { value: 7, label: 'July' },
                    { value: 8, label: 'August' },
                    { value: 9, label: 'September' },
                    { value: 10, label: 'October' },
                    { value: 11, label: 'November' },
                    { value: 12, label: 'December' },
                  ].map((month) => (
                    <MenuItem key={month.value} value={month.value} sx={{ fontSize: '0.8rem', py: 0.5 }}>{month.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Divider sx={{ my: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Emission Details</Typography>
            </Divider>

            {/* Category Selection */}
            <FormControl fullWidth required size="small">
              <InputLabel sx={{ fontSize: '0.8rem' }}>
                {scope === 'Scope 1' ? 'Emission Category' : 'Energy Type'}
              </InputLabel>
              <Select
                value={formData.category}
                label={scope === 'Scope 1' ? 'Emission Category' : 'Energy Type'}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                sx={{ fontSize: '0.8rem' }}
              >
                {scope === 'Scope 1'
                  ? SCOPE1_CATEGORIES.map((cat) => (
                      <MenuItem key={cat.value} value={cat.value} sx={{ fontSize: '0.8rem', py: 0.5 }}>{cat.label}</MenuItem>
                    ))
                  : SCOPE2_ENERGY_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value} sx={{ fontSize: '0.8rem', py: 0.5 }}>{type.label}</MenuItem>
                    ))
                }
              </Select>
            </FormControl>

            {/* Activity Selection (Scope 1 only) */}
            {scope === 'Scope 1' && formData.category && (
              <FormControl fullWidth required size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Activity Type</InputLabel>
                <Select
                  value={formData.activity}
                  label="Activity Type"
                  onChange={(e) => setFormData({ ...formData, activity: e.target.value })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {getActivitiesForCategory().map((activity) => (
                    <MenuItem key={activity.value} value={activity.value} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                      {activity.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Fuel/Source Type Selection (Scope 1 only) */}
            {scope === 'Scope 1' && formData.activity && (
              <FormControl fullWidth required size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Fuel / Source Type</InputLabel>
                <Select
                  value={formData.fuelType}
                  label="Fuel / Source Type"
                  onChange={(e) => setFormData({ ...formData, fuelType: e.target.value })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {getFuelTypes().map((fuel) => (
                    <MenuItem key={fuel.value} value={fuel.value} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                      {fuel.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Scope 2 Accounting Method */}
            {scope === 'Scope 2' && (
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Accounting Method</InputLabel>
                <Select
                  value={formData.method}
                  label="Accounting Method"
                  onChange={(e) => setFormData({ ...formData, method: e.target.value as Scope2Method })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  <MenuItem value="Location-Based" sx={{ fontSize: '0.8rem', py: 0.5 }}>
                    Location-Based
                  </MenuItem>
                  <MenuItem value="Market-Based" sx={{ fontSize: '0.8rem', py: 0.5 }}>
                    Market-Based
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        )}

        {/* Step 2: Activity Data */}
        {activeStep === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Selected Category Info */}
            <Paper sx={{ p: 1.5, backgroundColor: '#f9fafb', borderRadius: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6rem' }}>
                Selected
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827', mt: 0.25, fontSize: '0.8rem' }}>
                {scope === 'Scope 1'
                  ? `${SCOPE1_CATEGORIES.find(c => c.value === formData.category)?.label || formData.category} - ${formData.fuelType}`
                  : `${SCOPE2_ENERGY_TYPES.find(e => e.value === formData.category)?.label || formData.category}`
                }
              </Typography>
            </Paper>

            {/* Emission Factor Source Selection */}
            <Paper sx={{ p: 1.5, backgroundColor: '#f0f9ff', borderRadius: 1.5, border: '1px solid #bae6fd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Edit sx={{ fontSize: 14, color: '#0284c7' }} />
                <Typography variant="caption" sx={{ color: '#0284c7', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6rem' }}>
                  Emission Factor Source
                </Typography>
              </Box>

              <RadioGroup
                value={factorSourceType}
                onChange={(e) => handleFactorSourceChange(e.target.value as 'auto' | 'standard' | 'location' | 'custom')}
                sx={{ gap: 0.5 }}
              >
                {/* Auto Option */}
                <FormControlLabel
                  value="auto"
                  control={<Radio size="small" sx={{ py: 0.25 }} />}
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>Auto (Recommended)</Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: '#6b7280' }}>
                        {scope === 'Scope 2'
                          ? 'Uses location-based factor for your facility region'
                          : formData.category === 'mobile_combustion'
                            ? 'Uses EPA 2024 fuel-based factors'
                            : 'Uses DEFRA 2024 factors'}
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, alignItems: 'flex-start' }}
                />

                {/* Standard Factor Option (EPA for mobile combustion, DEFRA for others) */}
                {availableFactorSources.standard && (
                  <FormControlLabel
                    value="standard"
                    control={<Radio size="small" sx={{ py: 0.25 }} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                            {formData.category === 'mobile_combustion' ? 'EPA 2024' : 'DEFRA 2024'}
                          </Typography>
                          <Typography sx={{ fontSize: '0.6rem', color: '#6b7280' }}>
                            {formData.category === 'mobile_combustion'
                              ? 'US EPA fuel-based standard'
                              : 'UK Government standard'} • {availableFactorSources.standard.value.toFixed(5)} kg CO2e/{formData.activityUnit || 'unit'}
                          </Typography>
                        </Box>
                      </Box>
                    }
                    sx={{ m: 0, alignItems: 'flex-start' }}
                  />
                )}

                {/* Location-based Option (only for Scope 2) */}
                {scope === 'Scope 2' && availableFactorSources.location && (
                  <FormControlLabel
                    value="location"
                    control={<Radio size="small" sx={{ py: 0.25 }} />}
                    label={
                      <Box>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          Location-Based ({availableFactorSources.location.region})
                        </Typography>
                        <Typography sx={{ fontSize: '0.6rem', color: '#6b7280' }}>
                          {availableFactorSources.location.source} • {availableFactorSources.location.value.toFixed(5)} kg CO2e/{formData.activityUnit || 'unit'}
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, alignItems: 'flex-start' }}
                  />
                )}

                {/* Custom Option */}
                <FormControlLabel
                  value="custom"
                  control={<Radio size="small" sx={{ py: 0.25 }} />}
                  label={
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>Custom Factor</Typography>
                  }
                  sx={{ m: 0, alignItems: 'flex-start' }}
                />
              </RadioGroup>

              {/* Custom Factor Input */}
              {factorSourceType === 'custom' && (
                <TextField
                  label="Custom Emission Factor"
                  type="number"
                  fullWidth
                  size="small"
                  value={customFactorValue}
                  onChange={(e) => handleCustomFactorChange(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography sx={{ color: '#6b7280', fontSize: '0.7rem' }}>
                          kg CO2e/{formData.activityUnit || 'unit'}
                        </Typography>
                      </InputAdornment>
                    ),
                    sx: { fontSize: '0.8rem' },
                  }}
                  InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                  placeholder="Enter emission factor value"
                  sx={{ mt: 1 }}
                  helperText="Enter your organization's specific emission factor"
                  FormHelperTextProps={{ sx: { fontSize: '0.6rem' } }}
                />
              )}

              {/* Current Factor Display */}
              {emissionFactor !== null && factorSourceType !== 'custom' && (
                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0f2fe' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600, fontSize: '0.7rem' }}>
                      Active: {emissionFactor.toFixed(5)} kg CO2e/{formData.activityUnit || 'unit'}
                    </Typography>
                    <Tooltip title={`Source: ${emissionFactorSource}`}>
                      <Info sx={{ fontSize: 12, color: '#9ca3af' }} />
                    </Tooltip>
                  </Box>
                </Box>
              )}

              {loadingFactor && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CircularProgress size={10} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    Loading emission factors...
                  </Typography>
                </Box>
              )}
              {factorError && (
                <Alert severity="warning" sx={{ mt: 1, py: 0, '& .MuiAlert-message': { fontSize: '0.7rem' } }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{factorError}</Typography>
                </Alert>
              )}
            </Paper>

            {/* Unit Selection */}
            {scope === 'Scope 1' && getUnits().length > 1 && (
              <FormControl fullWidth required size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Unit of Measurement</InputLabel>
                <Select
                  value={formData.activityUnit}
                  label="Unit of Measurement"
                  onChange={(e) => setFormData({ ...formData, activityUnit: e.target.value })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {getUnits().map((unit) => (
                    <MenuItem key={unit} value={unit} sx={{ fontSize: '0.8rem', py: 0.5 }}>{unit}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Activity Amount"
              type="number"
              fullWidth
              required
              size="small"
              value={formData.activityAmount}
              onChange={(e) => setFormData({ ...formData, activityAmount: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography sx={{ color: '#6b7280', fontWeight: 500, fontSize: '0.75rem' }}>
                      {formData.activityUnit}
                    </Typography>
                  </InputAdornment>
                ),
                sx: { fontSize: '0.8rem' },
              }}
              InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
              placeholder="Enter quantity"
            />

            {scope === 'Scope 2' && formData.method === 'Market-Based' && (
              <TextField
                label="Renewable Energy Share"
                type="number"
                fullWidth
                size="small"
                value={formData.renewableShare}
                onChange={(e) => setFormData({ ...formData, renewableShare: e.target.value })}
                InputProps={{
                  endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.75rem' }}>%</Typography></InputAdornment>,
                  sx: { fontSize: '0.8rem' },
                }}
                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                placeholder="0-100"
                helperText="Enter percentage of renewable energy from your supplier"
                FormHelperTextProps={{ sx: { fontSize: '0.65rem' } }}
              />
            )}

            <TextField
              label="Notes (Optional)"
              multiline
              rows={2}
              fullWidth
              size="small"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              InputProps={{ sx: { fontSize: '0.8rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
              placeholder="Add any relevant notes..."
            />

            {/* Evidence Attachments */}
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <AttachFile sx={{ fontSize: 16, color: '#6b7280' }} />
                <Typography variant="body2" sx={{ color: '#374151', fontWeight: 500, fontSize: '0.8rem' }}>
                  Supporting Evidence (Optional)
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem', display: 'block', mb: 1 }}>
                Attach invoices, meter readings, or other supporting documents
              </Typography>
              <FileUploadZone
                files={attachments}
                onFilesChange={(files) => {
                  setAttachments(files);
                  // Auto-set to primary if evidence is attached
                  if (files.length > 0 && dataQuality === 'secondary') {
                    setDataQuality('primary');
                  }
                }}
                onUpload={handleFileUpload}
                onRemove={handleFileRemove}
                maxFiles={10}
                maxFileSize={50 * 1024 * 1024}
                maxTotalSize={200 * 1024 * 1024}
              />
            </Box>

            {/* Data Quality Selector */}
            <Paper sx={{ p: 1.5, backgroundColor: '#f0fdf4', borderRadius: 1.5, border: '1px solid #86efac' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <CheckCircle sx={{ fontSize: 16, color: '#16a34a' }} />
                <Typography variant="body2" sx={{ color: '#166534', fontWeight: 600, fontSize: '0.8rem' }}>
                  Data Quality
                </Typography>
                <Tooltip title="Data quality affects your ESG score. Primary = actual measurements/invoices, Secondary = calculated estimates, Tertiary = industry averages">
                  <Info sx={{ fontSize: 14, color: '#9ca3af', ml: 'auto' }} />
                </Tooltip>
              </Box>
              <RadioGroup
                value={dataQuality}
                onChange={(e) => setDataQuality(e.target.value as 'primary' | 'secondary' | 'tertiary')}
                sx={{ gap: 0.5 }}
              >
                <FormControlLabel
                  value="primary"
                  control={<Radio size="small" sx={{ py: 0.25 }} />}
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: attachments.length > 0 ? '#16a34a' : '#374151' }}>
                        Primary Data (Score: 5) {attachments.length > 0 && '✓ Recommended'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: '#6b7280' }}>
                        Direct measurements from invoices, meter readings, or bills
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, alignItems: 'flex-start' }}
                />
                <FormControlLabel
                  value="secondary"
                  control={<Radio size="small" sx={{ py: 0.25 }} />}
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                        Secondary Data (Score: 3)
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: '#6b7280' }}>
                        Calculated from activity data using standard emission factors
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, alignItems: 'flex-start' }}
                />
                <FormControlLabel
                  value="tertiary"
                  control={<Radio size="small" sx={{ py: 0.25 }} />}
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                        Tertiary Data (Score: 1)
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: '#6b7280' }}>
                        Industry averages, benchmarks, or rough estimates
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, alignItems: 'flex-start' }}
                />
              </RadioGroup>
            </Paper>
          </Box>
        )}

        {/* Step 3: Review */}
        {activeStep === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{ p: 1.5, backgroundColor: '#ecfdf5', borderRadius: 1.5, border: '1px solid #10b981' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Calculate sx={{ color: '#10b981', fontSize: 18 }} />
                <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 600, fontSize: '0.8rem' }}>
                  Calculated Emissions
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#10b981', fontSize: '1.25rem' }}>
                {calculateEmissions().toLocaleString(undefined, { maximumFractionDigits: 3 })}
                <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 400, color: '#6b7280', ml: 0.5 }}>
                  tCO2e
                </Typography>
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', fontSize: '0.65rem' }}>
                <strong>Formula:</strong> {formData.activityAmount} {formData.activityUnit} × {emissionFactor?.toFixed(5) || '?'} kg CO2e/{formData.activityUnit} ÷ 1000
              </Typography>
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mt: 0.25, fontSize: '0.65rem' }}>
                <strong>Source:</strong> {emissionFactorSource || 'DEFRA 2024'}
              </Typography>
            </Paper>

            <Paper sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid #e5e7eb' }}>
              <Typography variant="caption" sx={{ color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block', fontSize: '0.6rem' }}>
                Summary
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.6rem' }}>Facility</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationOn sx={{ color: '#4caf50', fontSize: 14 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827', fontSize: '0.75rem' }}>
                      {facilities.find(f => f.id === formData.facilityId)?.name || '-'}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.6rem' }}>Reporting Period</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827', fontSize: '0.75rem' }}>
                    {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][formData.month - 1]} {formData.year}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.6rem' }}>Category</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827', fontSize: '0.75rem' }}>
                    {scope === 'Scope 1'
                      ? SCOPE1_CATEGORIES.find(c => c.value === formData.category)?.label
                      : SCOPE2_ENERGY_TYPES.find(e => e.value === formData.category)?.label
                    }
                  </Typography>
                </Box>
                {scope === 'Scope 1' && (
                  <Box>
                    <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.6rem' }}>Fuel Type</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827', fontSize: '0.75rem' }}>{formData.fuelType || '-'}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.6rem' }}>Activity</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827', fontSize: '0.75rem' }}>{formData.activityAmount} {formData.activityUnit}</Typography>
                </Box>
                {scope === 'Scope 2' && (
                  <Box>
                    <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.6rem' }}>Method</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827', fontSize: '0.75rem' }}>{formData.method}</Typography>
                  </Box>
                )}
                {attachments.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.6rem' }}>Evidence</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AttachFile sx={{ fontSize: 14, color: '#6b7280' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827', fontSize: '0.75rem' }}>
                        {attachments.length} file{attachments.length !== 1 ? 's' : ''} attached
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>

      {/* Footer Actions */}
      <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
        <Button
          size="small"
          onClick={activeStep === 0 ? handleClose : handleBack}
          startIcon={activeStep > 0 ? <ArrowBack sx={{ fontSize: 16 }} /> : undefined}
          sx={{ color: '#6b7280', textTransform: 'none', fontSize: '0.8rem' }}
        >
          {activeStep === 0 ? 'Cancel' : 'Back'}
        </Button>

        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            size="small"
            onClick={handleNext}
            disabled={!canProceed()}
            endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
            sx={{
              backgroundColor: '#3b82f6',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8rem',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#2563eb', boxShadow: 'none' },
              '&:disabled': { backgroundColor: '#e5e7eb' },
            }}
          >
            Continue
          </Button>
        ) : (
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={!emissionFactor}
            startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
            sx={{
              backgroundColor: '#10b981',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8rem',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#059669', boxShadow: 'none' },
            }}
          >
            {isEditMode ? 'Update Entry' : 'Submit for Approval'}
          </Button>
        )}
      </Box>
    </Dialog>
  );
};
